import store from '@/store/store';
import InstallationService from '@/services/installation.service';
import { hasProp } from '@/utils/validate.utils';
import { parseString, parseNumber, parseBoolean } from '@/utils/sanitaze.utils';
import { consoleError } from '@/utils/console.utils';
import { WIFI_STATUS, ALTHERMA } from '@/app.config';

/**
 * @typedef {Object} WebServer
 * @property {String} id - ID del webserver
 * @property {String} name - Nombre del webserver
 * @property {String} mac - Mac del webserver
 * @property {String} wsver - Versión del webserver
 * @property {String} mver - Versión del modem
 * @property {String} ip - IP del webserver
 * @property {String} wifi - Nombre de la red wifi
 * @property {String} rssi - Intensidad de señal de la red wifi
 * @property {String} installationID - ID de la instalación a la que pertenece si está asociado a este usuario
 * @property {String} installationName - Nombre de la instalación a la que pertenece si está asociado a este usuario *
 * @property {Number} modbusaddr - Indica el puerto del modbus
 * @property {Boolean} isDeviceConnectedToUnit - Indica si el webserver tiene conexión con la unidad
 * @property {Boolean} isDeviceConnectedToWifi - Indica si el webserver está conectado a la red wifi
 * @property {Boolean} isDeviceConnectedToCloud - Indica si el webserver está conectado a la cloud
 * @property {Boolean} isDeviceAssociated - Indica si el webserver está asociado a algún usuario
 * @property {Boolean} isUserAllow - Indica si el usuario tiene permiso para utilizar el webserver
 */

/**
 * Da formato a los datos de un webserver
 *
 * @param {String} webserverID - Id de la instalación
 * @param {Object} webserverData - Datos de una instalación del backend
 * @return {WebServer} - Datos formateados de un webserver
 */
// eslint-disable-next-line consistent-return
const sanitizeWebserver = (webserverID, webserverName, webserverData) => {
  try {
    const webserver = {};

    webserver.webserverName = webserverName;
    webserver.altherma = ALTHERMA.some(prefix => webserverName.toUpperCase().startsWith(prefix));

    if (hasProp(webserverData, 'general') && hasProp(webserverData.general, 'mac')) {
      webserver.mac = parseString(webserverData.general.mac);
    }
    if (hasProp(webserverData, 'general') && hasProp(webserverData.general, 'wsver')) {
      webserver.wsver = parseString(webserverData.general.wsver);
    }
    if (hasProp(webserverData, 'general') && hasProp(webserverData.general, 'mver')) {
      webserver.mver = parseString(webserverData.general.mver);
    }
    if (hasProp(webserverData, 'network') && hasProp(webserverData.network, 'ip')) {
      webserver.ip = parseString(webserverData.network.ip);
    }
    if (hasProp(webserverData, 'general') && hasProp(webserverData.general, 'mstatus')) {
      webserver.isDeviceConnectedToUnit = parseBoolean(webserverData.general.mstatus);
    }
    if (hasProp(webserverData, 'wifi') && hasProp(webserverData.wifi, 'ssid')) {
      webserver.wifi = decodeURIComponent(webserverData.wifi.ssid);
    }
    if (hasProp(webserverData, 'wifi') && hasProp(webserverData.wifi, 'rssi')) {
      webserver.rssi = parseNumber(webserverData.wifi.rssi);
    }
    if (hasProp(webserverData, 'wifi') && hasProp(webserverData.wifi, 'status')) {
      const status = parseNumber(webserverData.wifi.status);
      webserver.isDeviceConnectedToWifi =
        status === WIFI_STATUS.WIFI_CONNECTED || status === WIFI_STATUS.CLOUD_CONNECTED;
      webserver.isDeviceConnectedToCloud = status === WIFI_STATUS.CLOUD_CONNECTED;
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'ledsoff')) {
      webserver.ledsoff = parseBoolean(webserverData.extrainfo.ledsoff);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'auxheat_enable')) {
      webserver.auxheat_enable = parseBoolean(webserverData.params.auxheat_enable);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'auxheat_ton')) {
      webserver.auxheat_ton = parseNumber(webserverData.params.auxheat_ton);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'auxheat_toff')) {
      webserver.auxheat_toff = parseNumber(webserverData.params.auxheat_toff);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'auxheat_tdelay')) {
      webserver.auxheat_tdelay = parseNumber(webserverData.params.auxheat_tdelay);
    }
    if (
      hasProp(webserverData, 'params') &&
      hasProp(webserverData.params, 'auxheat_lockoutenable')
    ) {
      webserver.auxheat_lockoutenable = parseBoolean(webserverData.params.auxheat_lockoutenable);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'auxheat_lockouttemp')) {
      webserver.auxheat_lockouttemp = parseNumber(webserverData.params.auxheat_lockouttemp);
    }
    if (
      hasProp(webserverData, 'params') &&
      hasProp(webserverData.params, 'auxheat_extheatsource')
    ) {
      webserver.auxheat_extheatsource = parseNumber(webserverData.params.auxheat_extheatsource);
    }
    if (
      hasProp(webserverData, 'params') &&
      hasProp(webserverData.params, 'auxheat_emergencyheat')
    ) {
      webserver.auxheat_emergencyheat = parseBoolean(webserverData.params.auxheat_emergencyheat);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 't1t2_enable')) {
      webserver.t1t2_enable = parseNumber(webserverData.params.t1t2_enable);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 't1t2_nc')) {
      webserver.t1t2_nc = parseNumber(webserverData.params.t1t2_nc);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 't1t2_ondelay')) {
      webserver.t1t2_ondelay = parseNumber(webserverData.params.t1t2_ondelay);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 't1t2_offdelay')) {
      webserver.t1t2_offdelay = parseNumber(webserverData.params.t1t2_offdelay);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'rs485_mode')) {
      webserver.rs485_mode = parseNumber(webserverData.params.rs485_mode);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'rs485_availablemodes')) {
      webserver.rs485_availablemodes = webserverData.params.rs485_availablemodes;
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'ecobeesb')) {
      webserver.ecobeesb = parseNumber(webserverData.params.ecobeesb);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'ecobeesbclientid')) {
      webserver.ecobeesbclientid = parseString(webserverData.params.ecobeesbclientid);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'ecobeesbclientsecret')) {
      webserver.ecobeesbclientsecret = parseString(webserverData.params.ecobeesbclientsecret);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'ecobeesbthermostatid')) {
      webserver.ecobeesbthermostatid = parseString(webserverData.params.ecobeesbthermostatid);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'ecobeesbstatus')) {
      webserver.ecobeesbstatus = parseNumber(webserverData.params.ecobeesbstatus);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'pelicansite')) {
      console.log('pelicansite: ', webserverData.params.pelicansite);
      webserver.pelicansite = parseString(webserverData.params.pelicansite);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'pelicanemail')) {
      console.log('pelicanemail: ', webserverData.params.pelicanemail);
      webserver.pelicanemail = parseString(webserverData.params.pelicanemail);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'pelicanpass')) {
      console.log('pelicanpass: ', webserverData.params.pelicanpass);
      webserver.pelicanpass = parseString(webserverData.params.pelicanpass);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'pelicanserial')) {
      console.log('pelicanserial: ', webserverData.params.pelicanserial);
      webserver.pelicanserial = parseString(webserverData.params.pelicanserial);
    }
    if (hasProp(webserverData, 'params') && hasProp(webserverData.params, 'pelicanstatus')) {
      console.log('pelicanstatus: ', webserverData.params.pelicanstatus);
      webserver.pelicanstatus = parseNumber(webserverData.params.pelicanstatus);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'modbusaddr')) {
      webserver.modbusaddr = parseNumber(webserverData.extrainfo.modbusaddr);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'modbusspeed')) {
      webserver.modbusspeed = parseNumber(webserverData.extrainfo.modbusspeed);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'modbusparity')) {
      webserver.modbusparity = parseNumber(webserverData.extrainfo.modbusparity);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'modbusbitstop')) {
      webserver.modbusbitstop = parseNumber(webserverData.extrainfo.modbusbitstop);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'bacneton')) {
      webserver.bacneton = parseBoolean(webserverData.extrainfo.bacneton);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'bacnetmac')) {
      webserver.bacnetmac = parseNumber(webserverData.extrainfo.bacnetmac);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'bacnetid')) {
      webserver.bacnetid = parseNumber(webserverData.extrainfo.bacnetid);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'bacnetmaxnum')) {
      webserver.bacnetmaxnum = parseNumber(webserverData.extrainfo.bacnetmaxnum);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'bacnetspeed')) {
      webserver.bacnetspeed = parseNumber(webserverData.extrainfo.bacnetspeed);
    }
    if (
      hasProp(webserverData, 'extrainfo') &&
      hasProp(webserverData.extrainfo, 'bacnetmaxframes')
    ) {
      webserver.bacnetmaxframes = parseNumber(webserverData.extrainfo.bacnetmaxframes);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'gpioalpha')) {
      webserver.gpioalpha = parseNumber(webserverData.extrainfo.gpioalpha);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'gpiobeta')) {
      webserver.gpiobeta = parseNumber(webserverData.extrainfo.gpiobeta);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'gpiominruntime')) {
      webserver.gpiominruntime = parseNumber(webserverData.extrainfo.gpiominruntime);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'gpiostoptime')) {
      webserver.gpiostoptime = parseNumber(webserverData.extrainfo.gpiostoptime);
    }
    if (hasProp(webserverData, 'extrainfo') && hasProp(webserverData.extrainfo, 'gpiofanspeed')) {
      webserver.gpiofanspeed = parseNumber(webserverData.extrainfo.gpiofanspeed);
    }
    if (hasProp(webserverData, 'services') && hasProp(webserverData.services, 'lapi')) {
      webserver.lapi = parseNumber(webserverData.services.lapi);
    }
    if (hasProp(webserverData, 'services') && hasProp(webserverData.services, 'venstar')) {
      webserver.venstar = parseNumber(webserverData.services.venstar);
    }
    if (hasProp(webserverData, 'services') && hasProp(webserverData.services, 'pelican')) {
      webserver.pelican = parseNumber(webserverData.services.pelican);
    }

    return webserver;
  } catch (error) {
    consoleError(`Error al parsear los datos del webserver ${webserverID}`);
    return null;
  }
};

/**
 * Comprueba si los datos de un webserver son válidos cuando se reciben por primera vez
 *
 * @param {Object} webserverData - Datos del webserver
 * @return {Webserver} - Datos formateados y validados del webserver
 */
// eslint-disable-next-line consistent-return
const validateWebserver = async (webserverID, webserverName, webserverData) => {
  const webserver = sanitizeWebserver(webserverID, webserverName, webserverData);

  webserver.id = webserverID;

  /**
   * Compruebo que tiene los datos mínimos
   */
  if (!webserver || !webserver.id || !webserver.mac) {
    consoleError(`Error al validar los datos del webserver ${webserverID}`);
    return null;
  }

  /**
   * Compruebo si el webserver está asociado
   * 1- Todo webserver que no esté asociado tiene el acceso permitido a cualquier usuario
   */
  const associated = await InstallationService.isDeviceAssociated({ mac: webserver.mac });
  webserver.isDeviceAssociated = associated;
  webserver.isUserAllow = !associated; // [1]

  /**
   * Si está asociado, compruebo si el usuario tiene permiso para gestionarlo
   * Busco entre sus instalaciones si tiene algún dispositivo con esa mac
   */
  if (associated) {
    const installations = store.state.unitsModule.installations;
    const installationID = Object.keys(installations).find(installation => {
      return Object.keys(installations[installation].devices).find(mac => webserver.mac === mac);
    });

    if (installationID) {
      const installation = store.state.unitsModule.installations[installationID];
      const device = installation.devices[webserver.mac];

      webserver.installationID = installationID;
      webserver.installationName = installation.name;
      webserver.name = device.name;
      webserver.isUserAllow = installation.type === 'advanced';
    }
  }

  return webserver;
};

export { sanitizeWebserver, validateWebserver };
